import { Select } from '@pwc/pwc-deals-ui';
import React, { useCallback } from 'react';
import classNames from 'classnames';
import { Constants } from '@pwc/pwc-deals-web-common';
import { FieldArray, useFormikContext } from 'formik';
import { useSelector } from 'react-redux';
import './ProjectRoleUsersField.scss';
import { ProjectRoleUserPill } from '../Pills/ProjectRoleUserPill';
import { UserSearchSelect } from '../UserSearchSelect/UserSearchSelect';
import { isProjectArchived } from '../../helpers/projectStatusesUtils';
import {
  getProjectStatus,
  useProjectsStatuses,
} from '../../hooks/useProjectsStatuses';
import { getProject } from '../../../features/project/projectSlice';

export const userPillId = (user) =>
  `${user.role.toLowerCase().split(' ').join('-')}-${user.email
    .toLowerCase()
    .split(/[\s,@.]+/)
    .join('-')}`;

export const ProjectRoleUsersField = ({
  title,
  description,
  role,
  validateNotAlreadyIn = Constants.USER_ROLES_LIST,
  allowMany = false,
  showEmail = false,
  allowRemoving = true,
  allowAdding = true,
  addAsPending = false,
  allowApprovePending = false,
  validators = [],
  searchTextBoxPlaceHolder = 'Name',
  addUsersToAdd,
  addUsersToDelete,
  addUsersToApprove,
  addUsersToChangeRole,
  withRoleChange,
  roles,
}) => {
  const project = useSelector(getProject);
  const projectsStates = useProjectsStatuses();
  const projectStatus = getProjectStatus(projectsStates, project.id);
  const { values } = useFormikContext();

  const handleUserSubmit = (arrayHelpers) => (user) => {
    arrayHelpers.push({
      ...user,
      pillId: userPillId(user),
      pendingApproval: addAsPending,
      newlyAdded: true,
    });
    addUsersToAdd(user);
  };

  const handleRemoval = useCallback(
    (arrayHelpers, user, index) =>
      allowRemoving
        ? () => {
            arrayHelpers.remove(index);
            addUsersToDelete(user);
          }
        : undefined,
    [allowRemoving, addUsersToDelete]
  );

  const handleTogglePending = useCallback(
    (arrayHelpers, user, index) =>
      user.wasPending
        ? () => {
            arrayHelpers.replace(index, {
              ...user,
              pendingApproval: !user.pendingApproval,
            });
            addUsersToApprove({
              ...user,
              pendingApproval: !user.pendingApproval,
            });
          }
        : undefined,
    [addUsersToApprove]
  );

  const showTextInput =
    allowAdding &&
    (!(values[role] || []).length || allowMany) &&
    (!isProjectArchived(projectStatus) || role === 'PwC Readonly');

  const roleIdPart = role.split(' ').join('-').toLowerCase();

  const usersAndIndex = values[role].map((user, index) => ({
    user,
    index,
  }));
  const approvedUsers = usersAndIndex.filter(
    ({ user }) => !user.pendingApproval
  );

  const pendingUsers = usersAndIndex.filter(({ user }) => user.pendingApproval);
  const unavailableUsers = validateNotAlreadyIn
    .map((key) => values[key])
    .flat();

  const onChangeRoleSelected = ({ target }, user, arrayHelpers, index) => {
    const newRole = target.value;
    arrayHelpers.replace(index, {
      ...user,
      newRole,
    });
    addUsersToChangeRole({ ...user, role: newRole });
  };

  const allowsManyClass =
    usersAndIndex.length && allowMany ? 'allows-many' : '';
  const hasUsersClass = usersAndIndex.length ? 'has-users' : '';

  const { isDeleteEnabled, isChangeEnabled, isCreateEnabled } = {
    isDeleteEnabled: true,
    isChangeEnabled: false,
    isCreateEnabled: true,
  };

  return (
    <FieldArray
      name={role}
      render={(arrayHelpers) => (
        <div
          className={`project-role-user-field ${allowsManyClass} ${hasUsersClass}`}
          data-testid={`project-user-role-${roleIdPart}`}
        >
          <div className="project-user-description-cell">
            <h4 data-testid={`project-user-role-title-${roleIdPart}`}>
              {title}
            </h4>
            <span data-testid={`project-user-role-description-${roleIdPart}`}>
              {description}
            </span>
          </div>
          {showTextInput && isCreateEnabled ? (
            <div className="user-serch-select-container">
              <UserSearchSelect
                role={role}
                onUserSelected={handleUserSubmit(arrayHelpers)}
                unavailableUsers={unavailableUsers}
                customValidators={validators}
                placeHolderText={searchTextBoxPlaceHolder}
              />
            </div>
          ) : null}
          {approvedUsers.length ? (
            <div>
              {approvedUsers.map(({ user, index }) => (
                <div
                  className={classNames('project-user-pill-container', {
                    'project-user-pill-container-with-select': !!withRoleChange,
                  })}
                  key={index}
                >
                  <ProjectRoleUserPill
                    key={user.pillId}
                    user={user}
                    showEmail={showEmail}
                    onClearClick={
                      isDeleteEnabled
                        ? handleRemoval(arrayHelpers, user, index)
                        : undefined
                    }
                    onTogglePending={
                      allowApprovePending
                        ? handleTogglePending(arrayHelpers, user, index)
                        : undefined
                    }
                  />
                  <div>{user.email}</div>
                  <div>{user.globalGradeName}</div>
                  {isChangeEnabled && !user.newlyAdded && (
                    <div>
                      <Select
                        value={user.newRole || user.role}
                        options={roles}
                        onChange={(e) =>
                          onChangeRoleSelected(e, user, arrayHelpers, index)
                        }
                        omitEmpty
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : null}
          {pendingUsers.length ? (
            <>
              <h4 className="project-user-pill-label">Pending approval</h4>
              <div>
                {pendingUsers.map(({ user, index }) => (
                  <div className="project-user-pill-container pending">
                    <ProjectRoleUserPill
                      key={user.pillId}
                      user={user}
                      showEmail={showEmail}
                      onClearClick={
                        isDeleteEnabled
                          ? handleRemoval(arrayHelpers, index)
                          : undefined
                      }
                      onTogglePending={
                        allowApprovePending
                          ? handleTogglePending(arrayHelpers, user, index)
                          : undefined
                      }
                    />
                    <div>{user.email}</div>
                    <div>{user.globalGradeName}</div>
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </div>
      )}
    />
  );
};
